import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';

import { SpinnerOverlay, Tooltip } from '@components/common';

import { useDownloadFile } from '@hooks/common/useDownloadFile';

import styles from './SelectOptionWithAvatar.module.scss';

interface ISelectOptionWithAvatarProps {
	id?: string | null;
	text?: string;
	isDisabled?: boolean;
}

const SelectOptionWithAvatar = ({
	id,
	text,
	isDisabled = false,
}: ISelectOptionWithAvatarProps) => {
	const [isTextTruncated, setIsTextTruncated] = useState(false);

	const descriptionRef = useRef<HTMLDivElement | null>(null);

	const isDisabledTooltip = !(isDisabled || (isTextTruncated && text));

	useEffect(() => {
		const element = descriptionRef.current;

		if (!element) return;

		const updateIsTextTruncated = () => {
			const truncated = element.scrollWidth - element.clientWidth > 1;

			setIsTextTruncated(truncated);
		};

		updateIsTextTruncated();

		const resizeObserver = new ResizeObserver(updateIsTextTruncated);

		resizeObserver.observe(element);

		window.addEventListener('resize', updateIsTextTruncated);

		return () => {
			resizeObserver.disconnect();
			window.removeEventListener('resize', updateIsTextTruncated);
		};
	}, [text]);

	const {
		downloadUrl,
		isLoadingDownloadURL,
	} = useDownloadFile({ id });

	return (
		<div className={styles.container}>
			<div
				className={cn(styles.avatar, { [styles.withoutImage]: !downloadUrl })}
			>
				{isLoadingDownloadURL && <SpinnerOverlay />}
				{downloadUrl && (
					<div
						className={styles.img}
						style={{
							backgroundImage: `url(${downloadUrl})`,
						}}
					/>)
				}
			</div>
			<Tooltip
				content={
					isDisabled
						? 'Organisation is not available now'
						: isTextTruncated && text
							? text
							: undefined
				}
				isDisabled={isDisabledTooltip}
			>
				<div
					ref={descriptionRef}
					className={styles.description}
				>
					{text}
				</div>
			</Tooltip>
		</div>
	);
};

export default SelectOptionWithAvatar;
